/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * /domain/wms/showcase — landing for the Unity ML warehouse simulation
 * work. Reads showcase_simulations from V2 Supabase (no is_published
 * column; RLS policy is USING (true), so all rows surface).
 *
 * Layout: Live + Roadmap.
 *   - Live section: rows with a populated video_url, prominent card.
 *   - In Development section: rows still pending video, subdued
 *     RoadmapCard. Demonstrates trajectory without overclaiming.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import MlArchitectureDiagram from '../../components/showcase/MlArchitectureDiagram';
import SimulationPlayer from '../../components/showcase/SimulationPlayer';
import RoadmapCard from '../../components/showcase/RoadmapCard';
import ShowcaseSiblingNav from '../../components/showcase/ShowcaseSiblingNav';
import v2Supabase from '../../lib/v2Supabase';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
      }}
    />
  </>
);

const SkeletonCard = () => (
  <div className="bg-white border border-brand-border rounded-brandCard overflow-hidden animate-pulse">
    <div className="w-full bg-brand-bgOff2" style={{ aspectRatio: '16 / 9' }} />
    <div className="p-5">
      <div className="h-5 bg-brand-bgOff2 rounded w-3/4 mb-3" />
      <div className="h-3 bg-brand-bgOff2 rounded mb-2" />
      <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
    </div>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-center text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
    {children}
  </p>
);

const WmsSimulationShowcase = () => {
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [simulations, setSimulations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('showcase_simulations')
        .select('*')
        .eq('domain', 'wms')
        .order('sequence_number')
        .execute();

      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[WmsSimulationShowcase] load failed:', error);
        setStatus('error');
        return;
      }
      setSimulations(data || []);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const liveSims = simulations.filter((s) => s.video_url);
  const roadmapSims = simulations.filter((s) => !s.video_url);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* Back link */}
        <div className="mt-10">
          <Link
            to="/domain/wms"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to WMS
          </Link>
        </div>

        {/* Hero */}
        <header className="text-center mt-10 sm:mt-12">
          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            <span className="block">WMS</span>
            <span className="block">ML Simulation</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Trained reinforcement-learning policy navigating a procedural
            warehouse. PPO + curiosity, 79,000 training steps, running on
            Unity ML-Agents with Isaac Sim zone embeddings.
          </p>
        </header>

        {/* Architecture diagram */}
        <MlArchitectureDiagram />

        {/* Loading */}
        {status === 'loading' && (
          <section className="mt-14 max-w-4xl mx-auto">
            <SkeletonCard />
          </section>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-8 max-w-xl mx-auto">
            <p className="text-base text-brand-text font-semibold">
              Couldn't load simulation scenarios.
            </p>
            <p className="mt-2 text-sm text-brand-muted">
              Please refresh the page and try again.
            </p>
          </div>
        )}

        {/* Live Simulation section */}
        {status === 'ok' && liveSims.length > 0 && (
          <section className="mt-14" aria-label="Live simulation">
            <SectionLabel>Live Simulation</SectionLabel>

            {liveSims.length === 1 ? (
              <div className="mt-6 max-w-4xl mx-auto">
                <SimulationPlayer simulation={liveSims[0]} />
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                {liveSims.map((s) => (
                  <SimulationPlayer key={s.id} simulation={s} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* In Development section */}
        {status === 'ok' && roadmapSims.length > 0 && (
          <section className="mt-16" aria-label="In development">
            <SectionLabel>In Development</SectionLabel>
            <p className="mt-3 text-center text-sm text-brand-muted leading-relaxed max-w-2xl mx-auto">
              Scenarios in active training and design. Currently optimizing
              the trained policy across additional warehouse operations.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              {roadmapSims.map((s) => (
                <RoadmapCard key={s.id} simulation={s} />
              ))}
            </div>
          </section>
        )}

        {/* Empty (no rows seeded at all) */}
        {status === 'ok' && simulations.length === 0 && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-10 max-w-2xl mx-auto">
            <div className="text-4xl mb-3" aria-hidden="true">🎬</div>
            <p className="text-lg text-brand-text font-semibold">
              Simulation scenarios coming soon.
            </p>
          </div>
        )}

        <ShowcaseSiblingNav currentPath={location.pathname} />

      </div>

      <PublicFooter />
    </div>
  );
};

export default WmsSimulationShowcase;
